/**
 * RefinementPanel - Multi-turn image refinement interface.
 *
 * Layout: the critique field spans the full width above a three-column
 * footer (left: refinement metadata / history; middle: References; right:
 * Apply Refinement button). The image preview lives above this panel in
 * the page-level grid, not inside it.
 *
 * Reference images:
 *  - Added via the Media Library picker → server assigns
 *    `ref-image-N` names automatically; no naming/rename UI.
 *  - Session reference marks (drawn by the user on the image preview) flow
 *    in through the `add_session_reference_mark` endpoint and appear in the
 *    same References list, labeled `ref-mark-N` with a small badge
 *    indicating the marker color.
 */
import { useState, useCallback, useMemo } from 'react';
import { useCreativeStudioStore, useRefinementInfo } from '@/store/creativeStudioStore';
import {
  useApplyRefinement,
  useEndSession,
  useRevertRefinement,
  useAddSessionReferenceImage,
  useRemoveSessionReferenceImage,
} from '@/hooks/queries/useCreativeStudioQueries';
import { useMediaImage } from '@/hooks/queries/useMediaQueries';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { TrashIcon, AISparkleIcon } from '@/components/ui/Icons';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/20/solid';
import '@/styles/tooltip.css';
import { ImageThumbnailWithActions } from '@/components/common/ImageThumbnailWithActions';
import { ImagePicker } from '@/components/media/ImagePicker';
import type { ReferenceImageInfo } from '@/types/creativeStudio';
import {
  MAX_REFINEMENT_REFERENCES,
  MARKER_YELLOW_HEX,
  MARKER_MAGENTA_HEX,
} from '@/config/creativeStudio';
import toast from 'react-hot-toast';

const MAX_CRITIQUE_LENGTH = 1000;
const MIN_CRITIQUE_LENGTH = 5;

/**
 * Refinement-session reference image thumbnail.
 *
 * Prefers the signed_url provided in the session payload. Falls back to
 * useMediaImage only when the server didn't provide a signed_url (legacy
 * response, or transient lookup failure). Session marks always provide a
 * signed_url and never fall back. The trash icon removes the ref server-side.
 */
function RefinementReferenceThumbnail({
  reference,
  index,
  onRemove,
  disabled,
}: {
  reference: ReferenceImageInfo;
  index: number;
  onRemove: () => void;
  disabled?: boolean;
}) {
  // Session marks aren't in the Media Library; never trigger a lookup.
  const shouldFetch = !reference.is_session_mark && !reference.signed_url;
  const { data: mediaImage, isLoading } = useMediaImage(
    shouldFetch ? reference.media_image_id : ''
  );
  const thumbnailUrl =
    reference.signed_url || mediaImage?.thumbnail_url || mediaImage?.signed_url;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center w-16">
        <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-pulse w-8 h-8 bg-gray-200 rounded" />
          </div>
        </div>
        <div className="mt-1 h-3 w-12 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  // Small dot marker so the user can distinguish a mark from a regular
  // reference at a glance, color-matched to the highlighter shade the
  // user drew with (auto-picked by MarkerOverlay based on local image
  // brightness). Inline style because Tailwind doesn't ship a
  // highlighter-yellow/magenta in its default palette.
  const markBadgeStyle =
    reference.mark_shade === 'yellow'
      ? { backgroundColor: MARKER_YELLOW_HEX }
      : reference.mark_shade === 'magenta'
        ? { backgroundColor: MARKER_MAGENTA_HEX }
        : null;

  return (
    <div className="flex flex-col items-center w-16">
      <div className="relative">
        <ImageThumbnailWithActions
          imageUrl={thumbnailUrl}
          alt={mediaImage?.original_filename || reference.name}
          size={{ width: 64, height: 64 }}
          onRemove={onRemove}
          removeLabel={`Remove reference ${reference.name}`}
          confirmTitle="Remove Reference"
          confirmMessage="Remove this reference from the session?"
          disabled={disabled}
          label={String(index + 1)}
        />
        {markBadgeStyle && (
          <span
            className="absolute top-0.5 right-0.5 w-2.5 h-2.5 rounded-full border border-white"
            style={markBadgeStyle}
            aria-label={`Reference mark drawn in ${reference.mark_shade} highlighter`}
            title={`Reference mark (${reference.mark_shade})`}
          />
        )}
      </div>
      <span
        className="mt-1 text-[11px] text-gray-600 truncate max-w-[64px] text-center"
        title={reference.name}
      >
        {reference.name}
      </span>
    </div>
  );
}

export function RefinementPanel() {
  const [critique, setCritique] = useState('');
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [revertTargetIndex, setRevertTargetIndex] = useState<number | null>(null);

  const [isRefPickerOpen, setIsRefPickerOpen] = useState(false);

  const {
    refinementSessionId,
    refinementSession,
    isRefining,
    setIsRefining,
    updateRefinementSession,
    endRefinementSession,
  } = useCreativeStudioStore();

  const refinementInfo = useRefinementInfo();
  const applyRefinementMutation = useApplyRefinement();
  const endSessionMutation = useEndSession();
  const revertMutation = useRevertRefinement();
  const addRefMutation = useAddSessionReferenceImage();
  const removeRefMutation = useRemoveSessionReferenceImage();

  // Reference images come from the server session payload so they stay in
  // sync with mid-refinement add/remove across reloads.
  const sessionReferenceImages: ReferenceImageInfo[] = useMemo(
    () => refinementSession?.reference_images ?? [],
    [refinementSession?.reference_images]
  );

  const handleRefPickerConfirm = useCallback(
    async (selectedFromPicker: string[]) => {
      setIsRefPickerOpen(false);
      if (!refinementSessionId) return;

      // Session marks should never be deselected via the Media Library
      // picker (they have synthetic ids that won't be in the picker's
      // available set). Restrict the diff to media-library refs so a picker
      // pass that omits a mark doesn't accidentally remove it.
      const libraryRefIds = sessionReferenceImages
        .filter((r) => !r.is_session_mark)
        .map((r) => r.media_image_id);
      const libraryRefIdSet = new Set(libraryRefIds);
      const selectedSet = new Set(selectedFromPicker);
      const removedIds = libraryRefIds.filter((id) => !selectedSet.has(id));
      const newIds = selectedFromPicker.filter((id) => !libraryRefIdSet.has(id));

      for (const id of removedIds) {
        try {
          const updated = await removeRefMutation.mutateAsync({
            sessionId: refinementSessionId,
            mediaImageId: id,
          });
          updateRefinementSession(updated);
        } catch (error) {
          console.error(`Failed to remove reference image ${id}:`, error);
          toast.error('Failed to remove one or more reference images');
        }
      }

      for (const id of newIds) {
        try {
          // Send name=null so the backend assigns the next
          // `ref-image-N` slot — the UI no longer surfaces a naming
          // step.
          const updated = await addRefMutation.mutateAsync({
            sessionId: refinementSessionId,
            params: { media_image_id: id, name: null },
          });
          updateRefinementSession(updated);
        } catch (error) {
          console.error(`Failed to add reference image ${id}:`, error);
          toast.error('Failed to add reference image');
        }
      }
    },
    [
      refinementSessionId,
      sessionReferenceImages,
      removeRefMutation,
      addRefMutation,
      updateRefinementSession,
    ]
  );

  const handleRemoveRef = useCallback(
    async (mediaImageId: string) => {
      if (!refinementSessionId) return;
      try {
        const updated = await removeRefMutation.mutateAsync({
          sessionId: refinementSessionId,
          mediaImageId,
        });
        updateRefinementSession(updated);
      } catch (error) {
        console.error('Failed to remove reference image:', error);
        toast.error('Failed to remove reference image');
      }
    },
    [refinementSessionId, removeRefMutation, updateRefinementSession]
  );

  // Handle apply refinement
  const handleApplyRefinement = useCallback(async () => {
    if (!refinementSessionId || critique.length < MIN_CRITIQUE_LENGTH) {
      return;
    }

    setIsRefining(true);

    try {
      const result = await applyRefinementMutation.mutateAsync({
        sessionId: refinementSessionId,
        params: { critique },
      });

      updateRefinementSession(result);
      // Intentionally keep the critique in the textarea so the user can
      // tweak and re-apply without retyping the whole instruction. They
      // can clear it via the explicit X button in the textarea footer.
    } catch (error) {
      console.error('Failed to apply refinement:', error);
    } finally {
      setIsRefining(false);
    }
  }, [
    refinementSessionId,
    critique,
    applyRefinementMutation,
    updateRefinementSession,
    setIsRefining,
  ]);

  // Handle end session
  const handleEndSession = useCallback(async () => {
    if (!refinementSessionId) return;

    try {
      await endSessionMutation.mutateAsync(refinementSessionId);
      endRefinementSession();
    } catch (error) {
      console.error('Failed to end session:', error);
    }
  }, [refinementSessionId, endSessionMutation, endRefinementSession]);

  // Open confirmation dialog for revert
  const handleRevertClick = useCallback((targetIndex: number) => {
    if (!refinementSessionId || isRefining || revertMutation.isPending) return;
    setRevertTargetIndex(targetIndex);
    setShowRevertConfirm(true);
  }, [refinementSessionId, isRefining, revertMutation.isPending]);

  // Handle confirmed revert to a previous refinement
  const handleRevertConfirm = useCallback(async () => {
    if (!refinementSessionId || revertTargetIndex === null) return;

    try {
      const result = await revertMutation.mutateAsync({
        sessionId: refinementSessionId,
        targetIndex: revertTargetIndex,
      });
      updateRefinementSession(result);
      toast.success(
        revertTargetIndex === 0
          ? 'Reverted to original image'
          : `Reverted to refinement ${revertTargetIndex}`
      );
    } catch (error) {
      console.error('Failed to revert:', error);
      toast.error('Failed to revert refinement');
    } finally {
      setShowRevertConfirm(false);
      setRevertTargetIndex(null);
    }
  }, [refinementSessionId, revertTargetIndex, revertMutation, updateRefinementSession]);

  const isValidCritique =
    critique.length >= MIN_CRITIQUE_LENGTH &&
    critique.length <= MAX_CRITIQUE_LENGTH;
  const canApply = isValidCritique && !isRefining && refinementSessionId && refinementInfo.canRefine;

  // Handle key press (Cmd/Ctrl+Enter to submit; plain Enter inserts a newline)
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canApply) {
        e.preventDefault();
        handleApplyRefinement();
      }
    },
    [handleApplyRefinement, canApply]
  );

  // Media Library picker passes only library-backed refs in selectedImageIds;
  // session marks have synthetic ids that wouldn't match anything in the
  // picker's catalog.
  const libraryRefIds = useMemo(
    () =>
      sessionReferenceImages
        .filter((r) => !r.is_session_mark)
        .map((r) => r.media_image_id),
    [sessionReferenceImages]
  );

  // If no session, show empty state
  if (!refinementSession) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <AISparkleIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Refinement</h3>
          <p className="text-sm text-gray-500 mb-4">
            Generate an image first, then click "Refine" to start iterating on it.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Refine Image</h2>
          <p className="text-sm text-gray-500">
            Refinement {refinementInfo.refinementCount} of {refinementInfo.maxRefinements}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleEndSession}
          disabled={isRefining}
          className="text-gray-500 hover:text-red-500"
        >
          <XMarkIcon className="h-4 w-4 mr-1" />
          End Session
        </Button>
      </div>

      {/* Context Info — hidden when there's no original prompt (e.g. the
          "Edit in Studio" entry point from the Media Library starts with an
          empty original_prompt, so the section would just read "N/A"). */}
      {refinementInfo.originalPrompt ? (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Original Prompt</h4>
          <p className="text-sm text-gray-700 line-clamp-2">{refinementInfo.originalPrompt}</p>
        </div>
      ) : null}

      {/* Refinement Critique Input — full width */}
      <div className="mb-4">
        <label
          htmlFor="refinement-critique"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          What would you like to change?
        </label>
        {/* Placeholder uses the current `ref-image-N` / `ref-mark-N`
            naming. The backend still accepts the legacy `reference-image-N`
            / `reference-mark-N` form for sessions created before the
            rename (see _resolve_reference_image_name / slot-finder in
            creative_studio_service.py), so old in-flight sessions don't
            break — but we only surface the new names to users here. */}
        <textarea
          id="refinement-critique"
          value={critique}
          onChange={(e) => setCritique(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Describe what you'd like to improve...
Examples:
• The text is too small, make it larger and bolder
• Place a chicken at ref-mark-1
• Place the logo in ref-image-1 at ref-mark-2's position
• Move the logo to ref-mark-3's position"
          rows={4}
          maxLength={MAX_CRITIQUE_LENGTH}
          disabled={isRefining || !refinementInfo.canRefine}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm
                     focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                     disabled:bg-gray-100 disabled:text-gray-500
                     placeholder:text-gray-400 resize-none"
        />
        <div className="flex justify-between items-center mt-1 text-xs text-gray-500">
          <span>Press Cmd/Ctrl+Enter to apply</span>
          <div className="flex items-center gap-2">
            <span>{critique.length} / {MAX_CRITIQUE_LENGTH}</span>
            <button
              type="button"
              onClick={() => setCritique('')}
              disabled={isRefining || critique.length === 0}
              aria-label="Clear refinement prompt"
              title="Clear prompt"
              className="p-0.5 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Two-column footer above a full-width Apply button. Mirrors the
          Generate tab's structure so the two views stay visually
          consistent:
            left  = Refinement History (parallels Aspect/Resolution)
            right = References
          Both columns at 6/6 so the option fields get all the horizontal
          space; the Apply button drops below at full width. */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 md:col-span-6">
          {refinementSession.edit_history.length > 0 ? (
            <>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Refinement History</h4>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {refinementSession.edit_history.map((item, idx) => {
                  const isLast = idx === refinementSession.edit_history.length - 1;
                  const canRevert =
                    isLast &&
                    refinementSession.edit_history.length > 1 &&
                    !isRefining &&
                    !revertMutation.isPending;

                  return (
                    <div key={item.index} className="relative group flex-shrink-0">
                      <div
                        className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                          isLast ? 'border-primary-500' : 'border-gray-200'
                        }`}
                        title={item.instruction || 'Original'}
                      >
                        <img
                          src={item.image.signed_url}
                          alt={`Refinement ${item.index}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute bottom-0.5 left-0.5 bg-black/60 text-white text-[10px] font-medium px-1 rounded z-10">
                        {item.index === 0 ? 'Original' : item.index}
                      </div>
                      {canRevert && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg">
                          <button
                            onClick={() => handleRevertClick(refinementSession.edit_history.length - 2)}
                            className="p-1.5 bg-white text-gray-700 rounded hover:bg-red-500 hover:text-white transition-colors"
                            title="Undo this refinement"
                            aria-label="Undo this refinement"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-xs text-gray-500 flex items-end h-full">
              {sessionReferenceImages.length} of {MAX_REFINEMENT_REFERENCES} references attached
            </div>
          )}
        </div>

        <div className="col-span-12 md:col-span-6">
          <div className="flex items-center gap-1 mb-2">
            <h4 className="text-sm font-medium text-gray-700">
              References ({sessionReferenceImages.length}/{MAX_REFINEMENT_REFERENCES})
            </h4>
            <div className="tooltip-container">
              <button
                type="button"
                aria-label="About references"
                aria-describedby="refinement-references-hint-tooltip"
                className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
              >
                <InformationCircleIcon className="h-4 w-4 text-gray-400" />
              </button>
              <span
                id="refinement-references-hint-tooltip"
                role="tooltip"
                className="tooltip-text tooltip-text-left"
                style={{ whiteSpace: 'normal', width: '20rem', maxWidth: '60vw', textAlign: 'left' }}
              >
                Add a reference image from your Media Library, or draw a mark on the image above using the marker tool — its color auto-picks for contrast with whatever's under your cursor. Then reference the name of the image or marker (provided under its thumbnail below) in your prompt.
              </span>
            </div>
          </div>
          {/* Thumbnails + trailing "+" tile, matching the Generate tab's
              layout. Hover/focus on the tile surfaces the longer
              "Add from Media Library" label via the shared tooltip CSS. */}
          <div className="flex flex-wrap gap-3">
            {sessionReferenceImages.map((ref, index) => (
              <RefinementReferenceThumbnail
                key={ref.media_image_id}
                reference={ref}
                index={index}
                onRemove={() => handleRemoveRef(ref.media_image_id)}
                disabled={isRefining}
              />
            ))}
            <button
              type="button"
              onClick={() => setIsRefPickerOpen(true)}
              disabled={
                isRefining ||
                sessionReferenceImages.length >= MAX_REFINEMENT_REFERENCES ||
                addRefMutation.isPending
              }
              aria-label={
                sessionReferenceImages.length >= MAX_REFINEMENT_REFERENCES
                  ? `Maximum reached (${MAX_REFINEMENT_REFERENCES})`
                  : 'Add from Media Library'
              }
              className="relative w-16 h-16 inline-flex items-center justify-center
                         border border-dashed border-gray-300 rounded-lg
                         text-gray-500 hover:border-gray-400 hover:text-gray-700
                         transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="h-6 w-6" />
              {/* Info icon pinned to the upper-right corner of the tile —
                  see ReferenceImagePicker for the same pattern and the
                  full rationale for `aria-hidden`. */}
              <span aria-hidden="true" className="absolute top-0.5 right-0.5">
                <span className="tooltip-container">
                  <span className="block text-gray-400">
                    <InformationCircleIcon className="h-3.5 w-3.5" />
                  </span>
                  <span
                    className="tooltip-text tooltip-text-right"
                    style={{ whiteSpace: 'normal', width: '12rem', maxWidth: '60vw', textAlign: 'left' }}
                  >
                    {sessionReferenceImages.length >= MAX_REFINEMENT_REFERENCES
                      ? `Maximum reached (${MAX_REFINEMENT_REFERENCES})`
                      : '+ Add from Media Library'}
                  </span>
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Full-width Apply button below the two-column row */}
      <div className="mt-4">
        <Button
          variant="primary"
          size="md"
          onClick={handleApplyRefinement}
          disabled={!canApply}
          mobileTouch
          className="w-full"
        >
          <AISparkleIcon className="w-6 h-6 mr-1.5 ml-0.5 -mt-1 flex-shrink-0" />
          {isRefining ? 'Refining…' : 'Apply'}
        </Button>
      </div>

      {!refinementInfo.canRefine && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          Maximum refinements reached. Save your image or start a new generation.
        </div>
      )}

      {/* The "Refining your image…" status now lives as an overlay on
          the image preview itself (see ImagePreview), which keeps the
          page from collapsing and shifting the Apply button. No status
          row needed here. */}

      <ConfirmDialog
        isOpen={showRevertConfirm}
        onClose={() => {
          setShowRevertConfirm(false);
          setRevertTargetIndex(null);
        }}
        onConfirm={handleRevertConfirm}
        title="Undo Refinement"
        message="Are you sure you want to undo the last refinement? This will revert to the previous version."
        confirmText="Undo"
        cancelText="Cancel"
        variant="danger"
        isLoading={revertMutation.isPending}
      />

      <ImagePicker
        isOpen={isRefPickerOpen}
        onClose={() => setIsRefPickerOpen(false)}
        selectedImageIds={libraryRefIds}
        onConfirm={handleRefPickerConfirm}
        // Subtract any session marks already attached. `selectedImageIds`
        // only contains library refs, so without this subtraction the
        // picker would allow up to MAX library refs even when marks are
        // already eating into the per-session reference budget, and the
        // backend would reject the request with a 400.
        maxSelection={Math.max(
          0,
          MAX_REFINEMENT_REFERENCES -
            sessionReferenceImages.filter((r) => r.is_session_mark).length
        )}
      />
    </Card>
  );
}
